import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { 
  ArrowLeftIcon,
  ExclamationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const ProductCategoryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    category_name: '',
    display_label: '',
    description: '',
    sort_order: 0,
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [initialLoading, setInitialLoading] = useState(isEditing);

  useEffect(() => {
    if (isEditing) {
      fetchCategory();
    }
  }, [id, isEditing]);

  const fetchCategory = async () => {
    try {
      setInitialLoading(true);
      const response = await api.get(`/products/categories/${id}`);
      setFormData({
        category_name: response.data.category_name || '',
        display_label: response.data.display_label || '',
        description: response.data.description || '',
        sort_order: response.data.sort_order || 0,
        is_active: response.data.is_active !== false
      });
    } catch (err) {
      console.error('Error fetching category:', err);
      setError('Failed to load category details');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear messages when user starts typing
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.category_name.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const payload = {
        category_name: formData.category_name.trim(),
        display_label: formData.display_label.trim() || formData.category_name.trim(),
        description: formData.description.trim() || null,
        sort_order: formData.sort_order,
        is_active: formData.is_active
      };

      if (isEditing) {
        await api.put(`/products/categories/${id}`, payload);
        setSuccess('Category updated successfully');
      } else {
        await api.post('/products/categories', payload);
        setSuccess('Category created successfully');
      }

      // Redirect after a short delay to show success message
      setTimeout(() => {
        navigate('/products/categories');
      }, 1500);

    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.response?.data?.error || `Failed to ${isEditing ? 'update' : 'create'} category`);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/products/categories')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Categories
          </button>
        </div>
        <h1 className="text-2xl font-semibold text-gray-900 mt-4">
          {isEditing ? 'Edit Product Category' : 'Add Product Category'}
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          {isEditing ? 'Update the category details below.' : 'Create a new category to organize your products.'}
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">{success}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white shadow sm:rounded-lg">
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          {/* Category Name */}
          <div>
            <label htmlFor="category_name" className="block text-sm font-medium text-gray-700">
              Category Name *
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="category_name"
                id="category_name"
                required
                value={formData.category_name}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="e.g., Integration Services"
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              A clear, descriptive name for this category
            </p>
          </div>

          {/* Display Label */}
          <div>
            <label htmlFor="display_label" className="block text-sm font-medium text-gray-700">
              Display Label
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="display_label"
                id="display_label"
                value={formData.display_label}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="e.g., Integration Services"
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              A clear, descriptive name for this category
            </p>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <div className="mt-1">
              <textarea
                name="description"
                id="description"
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Optional description of this category..."
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Optional description to help explain what products belong in this category
            </p>
          </div>

          {/* Sort Order */}
          <div>
            <label htmlFor="sort_order" className="block text-sm font-medium text-gray-700">
              Sort Order
            </label>
            <div className="mt-1">
              <input
                type="number"
                name="sort_order"
                id="sort_order"
                value={formData.sort_order}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              The order in which this category appears in the category list
            </p>
          </div>

          {/* Active Status */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="is_active"
                name="is_active"
                type="checkbox"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="is_active" className="font-medium text-gray-700">
                Active
              </label>
              <p className="text-gray-500">
                Active categories can be assigned to products. Inactive categories are hidden from selection.
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/products/categories')}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditing ? 'Update Category' : 'Create Category'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductCategoryForm; 